import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services';

export interface DiscountInfo {
  percentOff?: number;
  amountOff?: number;
  duration: string;
  durationInMonths?: number;
}

interface PlanSelectorCouponProps {
  onPromotionCodeApplied: (code: string, discount: DiscountInfo | null) => void;
  initialCode?: string;
}

interface AppliedPromotion {
  code: string;
  discount: DiscountInfo;
}

interface ValidationState {
  isValidating: boolean;
  error: string | null;
  validatedDiscount: DiscountInfo | null;
}

const PlanSelectorCoupon: React.FC<PlanSelectorCouponProps> = ({ onPromotionCodeApplied, initialCode }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [input, setInput] = useState('');
  const [validation, setValidation] = useState<ValidationState>({
    isValidating: false,
    error: null,
    validatedDiscount: null,
  });
  const [applied, setApplied] = useState<AppliedPromotion | null>(null);
  const autoApplied = useRef(false);

  const { organisationId } = useAuth();

  const openDialog = () => {
    setInput('');
    setValidation({ isValidating: false, error: null, validatedDiscount: null });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const validateCode = async () => {
    if (!input.trim()) {
      setValidation({
        isValidating: false,
        error: 'Please enter a promotion code',
        validatedDiscount: null,
      });
      return;
    }

    try {
      setValidation({ isValidating: true, error: null, validatedDiscount: null });

      if (!organisationId) {
        throw new Error('No organisation selected');
      }

      const response = await apiClient.get(
        `/organisations/${organisationId}/stripe/promotion-codes/validate?code=${encodeURIComponent(input.trim())}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to validate promotion code');
      }

      if (data.valid && data.promotionCode) {
        setValidation({
          isValidating: false,
          error: null,
          validatedDiscount: {
            percentOff: data.promotionCode.coupon.percentOff,
            amountOff: data.promotionCode.coupon.amountOff,
            duration: data.promotionCode.coupon.duration,
            durationInMonths: data.promotionCode.coupon.durationInMonths,
          },
        });
      } else {
        setValidation({
          isValidating: false,
          error: data.error || 'Invalid promotion code',
          validatedDiscount: null,
        });
      }
    } catch (err: any) {
      setValidation({
        isValidating: false,
        error: err.message || 'Failed to validate promotion code',
        validatedDiscount: null,
      });
    }
  };

  const applyCode = () => {
    if (validation.validatedDiscount && input.trim()) {
      const code = input.trim();
      setApplied({ code, discount: validation.validatedDiscount });
      onPromotionCodeApplied(code, validation.validatedDiscount);
      setDialogOpen(false);
    }
  };

  const removeCode = () => {
    setApplied(null);
    onPromotionCodeApplied('', null);
  };

  // Auto-validate and apply initial code (e.g. from referral signup)
  const autoValidateAndApply = useCallback(async (code: string) => {
    if (!organisationId || !code.trim()) return;
    try {
      const response = await apiClient.get(
        `/organisations/${organisationId}/stripe/promotion-codes/validate?code=${encodeURIComponent(code.trim())}`
      );
      const data = await response.json();
      if (response.ok && data.valid && data.promotionCode) {
        const discount: DiscountInfo = {
          percentOff: data.promotionCode.coupon.percentOff,
          amountOff: data.promotionCode.coupon.amountOff,
          duration: data.promotionCode.coupon.duration,
          durationInMonths: data.promotionCode.coupon.durationInMonths,
        };
        setApplied({ code, discount });
        onPromotionCodeApplied(code, discount);
      }
    } catch {
      // Silent failure — user can still apply manually
    }
  }, [organisationId, onPromotionCodeApplied]);

  useEffect(() => {
    if (initialCode && !autoApplied.current && organisationId) {
      autoApplied.current = true;
      autoValidateAndApply(initialCode);
    }
  }, [initialCode, organisationId, autoValidateAndApply]);

  const formatDiscount = (info: DiscountInfo) => {
    const parts = [];

    if (info.percentOff) parts.push(`${info.percentOff}% off`);
    if (info.amountOff) parts.push(`$${info.amountOff} off`);

    if (info.duration === 'repeating' && info.durationInMonths) {
      parts.push(`for ${info.durationInMonths} months`);
    } else if (info.duration === 'forever') {
      parts.push('forever');
    } else if (info.duration === 'once') {
      parts.push('on first invoice');
    }

    return parts.join(' ');
  };

  return (
    <>
      {/* Promotion Code Applied Alert */}
      {applied && (
        <Alert severity="success" sx={{ mt: 3 }} onClose={removeCode}>
          Promotion code <strong>{applied.code}</strong> applied! {formatDiscount(applied.discount)}
        </Alert>
      )}

      {/* Coupon Link */}
      {!applied && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Link component="button" variant="body2" onClick={openDialog} sx={{ cursor: 'pointer' }}>
            Have a coupon? Click to add
          </Link>
        </Box>
      )}

      {/* Coupon Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Promotion Code</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter your promotion code to receive a discount on your subscription.
          </DialogContentText>

          <TextField
            autoFocus
            fullWidth
            name="promotionCode"
            id="promotionCode"
            label="Promotion Code"
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            placeholder="PARTNER20"
            error={!!validation.error}
            helperText={validation.error}
            sx={{ mb: 2 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                validateCode();
              }
            }}
          />

          {validation.validatedDiscount && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight={600}>
                Valid promotion code!
              </Typography>
              <Typography variant="body2">
                {formatDiscount(validation.validatedDiscount)}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          {!validation.validatedDiscount ? (
            <Button
              onClick={validateCode}
              variant="contained"
              disabled={validation.isValidating || !input.trim()}
              startIcon={validation.isValidating ? <CircularProgress size={20} /> : null}
            >
              {validation.isValidating ? 'Validating...' : 'Validate'}
            </Button>
          ) : (
            <Button onClick={applyCode} variant="contained" color="success">
              Apply Code
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlanSelectorCoupon;
